import React, { createContext, useState, useContext, ReactNode } from 'react';
import { useShopifyProduct } from '../../../api/shopify/ShopifyProduct';
import {
  getInitialAccordionState,
  getShouldHideSyndicatedContent,
  getSyndicatedContentAccordionIndex,
} from './utils/utils';

type AccordionContext = {
  active: Array<string>;
  setActive: React.Dispatch<React.SetStateAction<Array<string>>>;
  syndicatedContentIndex: number;
  shouldHideSyndicatedContent: boolean;
};

const ProductAccordionContext = createContext<AccordionContext | undefined>(undefined);

const goToAccordion = (id: string, accordion: AccordionContext): void => {
  accordion.setActive([...new Set([...accordion.active, id])]);
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth' });
  }
};

type ProductAccordionProviderProps = {
  children: ReactNode;
};

const ProductAccordionProvider = ({ children }: ProductAccordionProviderProps): JSX.Element => {
  const { shopify, metafields } = useShopifyProduct();
  const { Product } = metafields.online_product.value;
  const sku = shopify.variants[0]?.sku;
  const shouldHideSyndicatedContent = getShouldHideSyndicatedContent(Product?.Brand);
  const syndicatedContentIndex = getSyndicatedContentAccordionIndex(sku, Product?.Brand);

  const [activeState, setActiveState] = useState(getInitialAccordionState(sku, Product?.Brand));

  return (
    <ProductAccordionContext.Provider
      value={{
        active: activeState,
        setActive: setActiveState as React.Dispatch<React.SetStateAction<Array<string>>>,
        syndicatedContentIndex: syndicatedContentIndex,
        shouldHideSyndicatedContent: shouldHideSyndicatedContent,
      }}
    >
      {children}
    </ProductAccordionContext.Provider>
  );
};

const useProductAccordion = (): AccordionContext => {
  const context = useContext(ProductAccordionContext);
  if (context === undefined) {
    throw new Error('useProductAccordion must be used within a ProductAccordionProvider');
  }
  return context;
};

export { ProductAccordionProvider, useProductAccordion, goToAccordion };
